// Billing status pill; colors: blue in-flight, green good, amber attention, red failed.

export interface Tone {
    bg: string;
    strike?: boolean;
}

const TONES: Record<string, Tone> = {
    pending: { bg: 'bg-gray-500' },
    processing: { bg: 'bg-blue-600' },
    created: { bg: 'bg-blue-600' },
    generated: { bg: 'bg-blue-600' },
    invoiced: { bg: 'bg-emerald-600' },
    paid: { bg: 'bg-emerald-600' },
    sent: { bg: 'bg-emerald-600' },
    open: { bg: 'bg-teal-600' },
    partial: { bg: 'bg-amber-600' },
    error: { bg: 'bg-rose-600' },
    canceled: { bg: 'bg-gray-400', strike: true },
    void: { bg: 'bg-gray-400', strike: true },
    available: { bg: 'bg-emerald-600' },
    expiring: { bg: 'bg-amber-600' },
    applied: { bg: 'bg-gray-500' },
    expired: { bg: 'bg-gray-400', strike: true }
};

const DEFAULT_TONE: Tone = { bg: 'bg-gray-500' };

interface StatusBadgeProps {
    status: string;
    // Lets a caller with its own status vocabulary reuse this rendering without
    // polluting the shared TONES map, where "open" already means something else.
    toneOverride?: Record<string, Tone>;
    // Display text for vocabularies the default capitalization mangles, e.g.
    // a purchase order's snake_case 'not_shipped'.
    label?: string;
}

export function StatusBadge({ status, toneOverride, label: labelOverride }: StatusBadgeProps) {
    const tone = toneOverride?.[status] ?? TONES[status] ?? DEFAULT_TONE;
    const label = labelOverride ?? status.charAt(0).toUpperCase() + status.slice(1);
    return (
        <span
            className={`inline-flex items-center max-w-[130px] gap-1.5 rounded-md px-2.5 py-1 text-[11px] font-semibold text-white ${tone.bg}`}
        >
            <span className="w-1.5 h-1.5 rounded-full bg-white/40 flex-shrink-0" />
            <span title={label} className={`truncate ${tone.strike ? 'line-through' : ''}`}>
                {label}
            </span>
        </span>
    );
}

export function SourceChip({ source }: { source: string }) {
    const isSystem = source === 'system';
    const label = isSystem ? 'System' : 'Manual';
    const cls = isSystem
        ? 'bg-gray-50 text-gray-500 border border-gray-200'
        : 'bg-gray-100 text-gray-600';
    return (
        <span className={`inline-flex text-[11px] font-medium px-2 py-0.5 rounded-full ${cls}`}>
            {label}
        </span>
    );
}
